import { useEffect, useRef } from 'react';
import './LunarLevelImage.css';

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
}

export function LunarLevelImage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const spaceshipImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const width = 400;
    const height = 250;
    canvas.width = width;
    canvas.height = height;

    const centerX = width / 2;
    const centerY = height / 2 - 20;
    const moonRadius = 80;

    // Load spaceship image from get_icon API (S3 via Lambda)
    const loadSpaceshipImage = async () => {
      try {
        const { apiClient } = await import('@/services/api');
        const dataUrl = await apiClient.getIcon('spaceship-top-8bit.png');
        
        const spaceshipImage = new Image();
        spaceshipImageRef.current = spaceshipImage;
        
        // Wait for image to load before starting animation
        spaceshipImage.onload = () => {
          animate();
        };
        spaceshipImage.onerror = (e) => {
          console.error('Failed to load spaceship sprite:', e);
        };
        spaceshipImage.src = dataUrl;
        
        // If already loaded (cached), start animation immediately
        if (spaceshipImage.complete) {
          animate();
        }
      } catch (error) {
        console.error('Error loading spaceship sprite:', error);
      }
    };
    
    loadSpaceshipImage();

    // Generate stars
    const generateStars = () => {
      const stars: Star[] = [];
      for (let i = 0; i < 50; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.5 + 0.5,
        });
      }
      return stars;
    };

    starsRef.current = generateStars();

    // Draw moon with craters
    const drawMoon = () => {
      // Moon body
      ctx.fillStyle = '#888';
      ctx.beginPath();
      ctx.arc(centerX, centerY, moonRadius, 0, Math.PI * 2);
      ctx.fill();

      // Craters (darker circles)
      const craters = [
        { x: centerX - 30, y: centerY - 20, r: 12 },
        { x: centerX + 25, y: centerY - 15, r: 18 },
        { x: centerX - 15, y: centerY + 25, r: 10 },
        { x: centerX + 15, y: centerY + 10, r: 15 },
        { x: centerX - 5, y: centerY - 40, r: 8 },
      ];

      ctx.fillStyle = '#666';
      craters.forEach((crater) => {
        ctx.beginPath();
        ctx.arc(crater.x, crater.y, crater.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // Add subtle maria (dark patches)
      ctx.fillStyle = '#777';
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.arc(centerX + 35, centerY + 30, 25, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    // Orbital mechanics
    let orbitAngle = 0;
    const orbitSpeed = 0.008; // Slow, realistic orbital speed
    const orbitRadiusX = 95; // Horizontal orbit radius - much tighter to cross moon surface
    const orbitRadiusY = 70; // Vertical orbit radius - tighter to cross over moon
    
    // Orbital perturbation parameters for slight randomness
    const perturbationFrequency1 = 0.7; // Slower wave
    const perturbationFrequency2 = 1.9; // Faster wave
    const perturbationAmplitude = 4; // Small variations in position
    
    // Calculate spaceship position in 3D space with slight random perturbations
    const getSpaceshipPosition = (angle: number, time: number) => {
      // Add subtle sine-wave perturbations at different frequencies for natural variation
      // This simulates micro-corrections and orbital irregularities
      const perturbX = Math.sin(time * perturbationFrequency1 + angle * 2) * perturbationAmplitude;
      const perturbY = Math.cos(time * perturbationFrequency2 + angle * 1.5) * perturbationAmplitude;
      
      // Elliptical orbit around moon with slight perturbations
      const x = centerX + Math.cos(angle) * orbitRadiusX + perturbX;
      const y = centerY + Math.sin(angle) * orbitRadiusY + perturbY;
      
      // Calculate z-depth based on vertical position (sin)
      // When sin is negative (top half), ship is behind moon (farther from camera)
      // When sin is positive (bottom half), ship is in front of moon (closer to camera)
      const zDepth = Math.sin(angle) * 0.5 + 0.5; // 0 (far/behind) to 1 (near/front)
      
      return { x, y, zDepth, angle };
    };

    // Draw spaceship with perspective scaling and correct rotation
    const drawSpaceship = (x: number, y: number, zDepth: number, angle: number) => {
      if (!spaceshipImageRef.current || !spaceshipImageRef.current.complete) return;

      // Scale based on depth (0.4 to 1.3 range for more dramatic effect)
      // Far away = small (0.4), close = large (1.3)
      const scale = 0.4 + zDepth * 0.9;
      const baseSize = 24; // Base size of spaceship sprite
      const size = baseSize * scale;

      ctx.save();
      
      // Opacity based on depth (fade when far away)
      ctx.globalAlpha = 0.6 + zDepth * 0.4;

      // Calculate velocity direction (tangent to orbit)
      // The derivative of position gives velocity direction
      const velocityX = -Math.sin(angle) * orbitRadiusX;
      const velocityY = Math.cos(angle) * orbitRadiusY;
      
      // Calculate rotation angle to point in direction of motion
      // atan2 gives angle of velocity vector
      // Add PI/2 (90 degrees) because the spaceship sprite points upward
      // Add PI (180 degrees) to flip it to face forward
      const rotation = Math.atan2(velocityY, velocityX) + Math.PI / 2;
      
      ctx.translate(x, y);
      ctx.rotate(rotation);
      
      // Draw spaceship centered
      ctx.drawImage(
        spaceshipImageRef.current,
        -size / 2,
        -size / 2,
        size,
        size
      );
      
      ctx.restore();
    };

    // Check if spaceship is behind the moon
    const isSpaceshipBehindMoon = (x: number, y: number, zDepth: number) => {
      // If zDepth < 0.5, spaceship is on far side (behind)
      if (zDepth < 0.5) {
        const dx = x - centerX;
        const dy = y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Check if spaceship would be occluded by moon (within moon radius)
        return distance < moonRadius + 10; // Add small buffer for visual overlap
      }
      return false;
    };

    let animationFrame = 0;

    // Animation loop
    const animate = () => {
      // Clear canvas
      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(0, 0, width, height);

      // Draw twinkling stars
      starsRef.current.forEach((star) => {
        const twinkle = Math.sin(animationFrame * 0.05 + star.x) * 0.3 + 0.7;
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * twinkle})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Update orbital position
      orbitAngle += orbitSpeed;
      const time = animationFrame * 0.01; // Slower time scale for perturbations
      const shipPos = getSpaceshipPosition(orbitAngle, time);

      // Determine rendering order based on depth
      const isBehind = isSpaceshipBehindMoon(shipPos.x, shipPos.y, shipPos.zDepth);

      if (isBehind) {
        // Draw spaceship first (behind moon)
        drawSpaceship(shipPos.x, shipPos.y, shipPos.zDepth, shipPos.angle);
        // Then draw moon on top
        drawMoon();
      } else {
        // Draw moon first
        drawMoon();
        // Then draw spaceship on top (in front)
        drawSpaceship(shipPos.x, shipPos.y, shipPos.zDepth, shipPos.angle);
      }

      animationFrame++;
      requestAnimationFrame(animate);
    };

    // Animation is started in loadSpaceshipImage() after image loads

    // Cleanup
    return () => {
      // Animation will stop when component unmounts
    };
  }, []);

  return (
    <div className="lunar-level-image">
      <canvas ref={canvasRef} className="lunar-canvas" />
    </div>
  );
}

